import React from 'react';
import { Link, Typography, TypographyProps } from '@mui/material';

interface LinkifiedTextProps extends Omit<TypographyProps, 'children'> {
  text?: string | null;
  clampLines?: number;
  stopLinkPropagation?: boolean;
}

type TextSegment = {
  type: 'text' | 'link';
  value: string;
};

const URL_PATTERN = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TRAILING_PUNCTUATION = new Set(['.', ',', '!', '?', ';', ':']);
const CLOSING_BRACKETS: Record<string, string> = {
  ')': '(',
  ']': '[',
  '}': '{',
};

const normalizeExternalUrl = (value: string) => (
  value.toLowerCase().startsWith('www.') ? `https://${value}` : value
);

const trimTrailingPunctuation = (value: string) => {
  let endIndex = value.length;

  while (endIndex > 0) {
    const lastCharacter = value[endIndex - 1];

    if (TRAILING_PUNCTUATION.has(lastCharacter)) {
      endIndex -= 1;
      continue;
    }

    const openingBracket = CLOSING_BRACKETS[lastCharacter];

    if (!openingBracket) {
      break;
    }

    const candidate = value.slice(0, endIndex - 1);
    const openingCount = Array.from(candidate).filter((character) => character === openingBracket).length;
    const closingCount = Array.from(candidate).filter((character) => character === lastCharacter).length;

    if (closingCount >= openingCount) {
      endIndex -= 1;
      continue;
    }

    break;
  }

  return {
    linkValue: value.slice(0, endIndex),
    trailingText: value.slice(endIndex),
  };
};

const toSegments = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  let currentIndex = 0;

  text.replace(URL_PATTERN, (match, _capture, offset: number) => {
    if (offset > currentIndex) {
      segments.push({
        type: 'text',
        value: text.slice(currentIndex, offset),
      });
    }

    const { linkValue, trailingText } = trimTrailingPunctuation(match);

    if (linkValue) {
      segments.push({ type: 'link', value: linkValue });
    }

    if (trailingText) {
      segments.push({ type: 'text', value: trailingText });
    }

    currentIndex = offset + match.length;

    return match;
  });

  if (currentIndex < text.length) {
    segments.push({
      type: 'text',
      value: text.slice(currentIndex),
    });
  }

  return segments;
};

const LinkifiedText: React.FC<LinkifiedTextProps> = ({
  text,
  clampLines,
  stopLinkPropagation = false,
  sx,
  ...typographyProps
}) => {
  if (!text) {
    return null;
  }

  const handleLinkClick = stopLinkPropagation
    ? (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
    }
    : undefined;

  const typographySx = [
    {
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      ...(clampLines ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: clampLines,
        WebkitBoxOrient: 'vertical',
      } : null),
    },
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];

  return (
    <Typography {...typographyProps} sx={typographySx}>
      {toSegments(text).map((segment, index) => {
        if (segment.type === 'text') {
          return <React.Fragment key={`text-${index}`}>{segment.value}</React.Fragment>;
        }

        return (
          <Link
            key={`link-${index}`}
            href={normalizeExternalUrl(segment.value)}
            target="_blank"
            rel="noopener noreferrer"
            color="inherit"
            underline="always"
            onClick={handleLinkClick}
            sx={{ overflowWrap: 'anywhere' }}
          >
            {segment.value}
          </Link>
        );
      })}
    </Typography>
  );
};

export default LinkifiedText;